import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { onAnimationEnd } from '@theme/utilities';
import { shouldShowEmailSignup } from './unbridled-email-signup-logic-v3.js';

const DISMISSED_AT_KEY = 'unbridledEmailSignupDismissedAtV1';
const SUBSCRIBED_KEY = 'unbridledEmailSignupSubscribedV1';
const RETURN_STATE_KEY = 'unbridledEmailSignupReturnStateV1';
const STABILITY_DELAY_MS = 350;

class UnbridledEmailSignup extends DialogComponent {
  #observer;
  #stabilityTimer;
  #isSuccess = false;
  #hasPresentedResponse = false;
  #previousFocus;
  #closingForAgeGate = false;
  #form;
  #submitButton;
  #submitButtonText = '';
  #isSubmitting = false;
  #submitFrame;
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#isSuccess = this.dataset.postedSuccessfully === 'true';
    this.#form = this.closest('form');
    this.#submitButton = this.querySelector('.unbridled-email-signup__submit');
    this.#submitButtonText = this.#submitButton?.textContent.trim() || 'Unlock Your Discount';
    this.addEventListener('click', this.#handleAction);
    this.addEventListener('dialog:close', this.#handleClosed);
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.#prepareSubmitFrame();
    this.#form?.addEventListener('submit', this.#handleNativeSubmit, true);

    const isThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);
    if (isThemeEditor) return;

    this.#restoreReturnState();

    if (this.#isSuccess) {
      this.#writeStorage(SUBSCRIBED_KEY, 'true');
    }

    this.#observer = new MutationObserver(this.#scheduleEvaluation);
    this.#observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'hidden', 'open', 'style'],
    });

    this.#scheduleEvaluation();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleAction);
    this.removeEventListener('dialog:close', this.#handleClosed);
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    this.#form?.removeEventListener('submit', this.#handleNativeSubmit, true);
    this.#submitFrame?.removeEventListener('load', this.#handleSubmitFrameLoad);
    this.#observer?.disconnect();
    window.clearTimeout(this.#stabilityTimer);
    super.disconnectedCallback();
  }

  #handleAction = (event) => {
    if (!(event.target instanceof Element)) return;

    if (!event.target.closest('[data-email-signup-close]')) return;

    this.closeDialog();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleNativeSubmit = () => {
    if (this.#isSubmitting) return;
    this.#setSubmitting(true);
    this.#showError('');
    this.#rememberReturnState();
  };

  #prepareSubmitFrame() {
    if (!this.#form) return;

    this.#submitFrame = document.createElement('iframe');
    this.#submitFrame.name = `${this.#form.id || 'UnbridledGlobalEmailSignup'}ResponseFrame`;
    this.#submitFrame.title = '';
    this.#submitFrame.hidden = true;
    this.#submitFrame.setAttribute('aria-hidden', 'true');
    this.#submitFrame.addEventListener('load', this.#handleSubmitFrameLoad);
    this.#form.target = this.#submitFrame.name;
    this.append(this.#submitFrame);
  }

  #handleSubmitFrameLoad = () => {
    if (!this.#isSubmitting) return;
    this.#presentSuccess();
  };

  #presentSuccess() {
    const offer = this.querySelector('[data-email-signup-offer]');
    const success = this.querySelector('[data-email-signup-success]');

    this.#isSuccess = true;
    this.#hasPresentedResponse = true;
    this.dataset.postedSuccessfully = 'true';
    this.dataset.hasErrors = 'false';
    if (offer) offer.hidden = true;
    if (success) success.hidden = false;
    this.refs.dialog.setAttribute('aria-labelledby', 'UnbridledEmailSignupSuccessTitle');
    this.refs.dialog.setAttribute('aria-describedby', 'UnbridledEmailSignupSuccessDescription');
    this.#writeStorage(SUBSCRIBED_KEY, 'true');
    this.#removeReturnState();
    this.#setSubmitting(false);
    this.#focusInitialState();
  }

  #handleClosed = () => {
    if (this.#closingForAgeGate) {
      this.#closingForAgeGate = false;
      this.#scheduleEvaluation();
    } else if (!this.#isSuccess) {
      this.#writeStorage(DISMISSED_AT_KEY, String(Date.now()));
    }

    if (this.#previousFocus instanceof HTMLElement && this.#previousFocus.isConnected) {
      this.#previousFocus.focus({ preventScroll: true });
    }
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    window.requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.show();
      this.dataset.open = '';
      delete this.dataset.closing;
      this.dispatchEvent(new DialogOpenEvent());
      this.addEventListener('keydown', this.#handleDialogKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('keydown', this.#handleDialogKeyDown);
    this.dataset.closing = '';
    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    delete this.dataset.open;
    delete this.dataset.closing;
    this.dispatchEvent(new DialogCloseEvent());
  };

  #handleDialogKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  #scheduleEvaluation = () => {
    if (!document.querySelector('#agp__root')) return;

    if (this.refs.dialog.open) {
      if (this.#hasVisibleAgeGate() && !this.#closingForAgeGate) {
        this.#closingForAgeGate = true;
        this.closeDialog();
      }
      return;
    }

    window.clearTimeout(this.#stabilityTimer);

    if (this.#hasVisibleBlockingDialog()) return;

    this.#stabilityTimer = window.setTimeout(() => {
      if (this.refs.dialog.open || this.#hasVisibleBlockingDialog()) return;

      const subscribed = this.#isSuccess || this.#readStorage(SUBSCRIBED_KEY) === 'true';
      const dismissedAt = this.#readStorage(DISMISSED_AT_KEY);
      const hasResponseToShow = this.#isSuccess || this.dataset.hasErrors === 'true';

      if (
        !shouldShowEmailSignup({
          subscribed,
          dismissedAt,
          hasResponse: hasResponseToShow,
          responsePresented: this.#hasPresentedResponse,
          now: Date.now(),
        })
      ) {
        return;
      }

      this.#previousFocus = document.activeElement;
      if (!hasResponseToShow) this.#rememberReturnState();
      this.showDialog();
      if (hasResponseToShow) this.#hasPresentedResponse = true;
      this.#focusInitialState();
    }, STABILITY_DELAY_MS);
  };

  #hasVisibleAgeGate() {
    const ageGateElements = document.querySelectorAll(
      '#agp__root .agp__wrapper, #agp__root .agp__container, #agp__root #agp_row'
    );

    return [...ageGateElements].some((element) => this.#isVisiblyBlocking(element));
  }

  #hasVisibleBlockingDialog() {
    const blockers = [
      ...document.querySelectorAll(
        '#agp__root .agp__wrapper, #agp__root .agp__container, #agp__root #agp_row'
      ),
      ...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]'),
    ];

    return blockers.some((element) => element && element !== this.refs.dialog && this.#isVisiblyBlocking(element));
  }

  #isVisiblyBlocking(element) {
    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      Number(styles.opacity || 1) > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  #focusInitialState() {
    window.requestAnimationFrame(() => {
      const focusTarget = this.querySelector(
        this.#isSuccess
          ? '[data-email-signup-success] h2'
          : this.dataset.hasErrors === 'true'
            ? '.unbridled-email-signup__error'
            : 'input[type="email"]'
      );

      focusTarget?.focus({ preventScroll: true });
    });
  }

  #setSubmitting(isSubmitting) {
    this.#isSubmitting = isSubmitting;
    if (!this.#submitButton) return;

    this.#submitButton.disabled = isSubmitting;
    this.#submitButton.setAttribute('aria-busy', String(isSubmitting));
    this.#submitButton.textContent = isSubmitting ? 'Joining…' : this.#submitButtonText;
  }

  #showError(message) {
    const error = this.querySelector('[data-email-signup-error]');
    if (!error) return;

    error.textContent = message;
    error.hidden = !message;
    if (message) error.focus({ preventScroll: true });
  }

  #rememberReturnState() {
    try {
      if (window.sessionStorage.getItem(RETURN_STATE_KEY)) return;

      window.sessionStorage.setItem(
        RETURN_STATE_KEY,
        JSON.stringify({
          url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          scrollY: window.scrollY,
        })
      );
    } catch {
      // Session storage can be unavailable in strict privacy modes.
    }
  }

  #restoreReturnState() {
    const currentUrl = new URL(window.location.href);
    const isNativeSignupReturn =
      this.#isSuccess &&
      currentUrl.searchParams.get('customer_posted') === 'true' &&
      currentUrl.hash === `#${this.#form?.id}`;

    if (!isNativeSignupReturn) return;

    let destination = '';
    let scrollY = 0;

    try {
      const storedState = JSON.parse(window.sessionStorage.getItem(RETURN_STATE_KEY) || 'null');
      const storedUrl = new URL(storedState?.url || '/', window.location.origin);

      if (storedUrl.origin === window.location.origin) {
        destination = `${storedUrl.pathname}${storedUrl.search}${storedUrl.hash}`;
      }

      const storedScrollY = Number(storedState?.scrollY);
      if (Number.isFinite(storedScrollY) && storedScrollY >= 0) scrollY = storedScrollY;
    } catch {
      // Fall through to the sanitized current URL.
    }

    if (!destination) {
      currentUrl.searchParams.delete('customer_posted');
      currentUrl.hash = '';
      destination = `${currentUrl.pathname}${currentUrl.search}`;
    }

    window.history.replaceState(window.history.state, '', destination);
    this.#removeReturnState();

    window.requestAnimationFrame(() => {
      window.scroll({ top: scrollY, left: 0, behavior: 'instant' });
    });
  }

  #removeReturnState() {
    try {
      window.sessionStorage.removeItem(RETURN_STATE_KEY);
    } catch {
      // Session storage can be unavailable in strict privacy modes.
    }
  }

  #readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  #writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable in strict privacy modes; the form still works.
    }
  }
}

if (!customElements.get('unbridled-email-signup')) {
  customElements.define('unbridled-email-signup', UnbridledEmailSignup);
}
